import { useState, useEffect, useRef } from 'react';
import settingsService from '../services/settings.service';
import { buildApiUrl, getTenantAlias } from '../config/api';

/**
 * Custom hook to fetch and return customer logo URL
 * Falls back to default LlamaSee logo if no customer logo is found
 * Prevents flickering by preloading the customer logo before switching
 *
 * @returns {Object} { logoUrl: string, handleImageError: function }
 */
export const useCustomerLogo = () => {
  const defaultLogo = '/static/images/llamasee_logo.svg';
  // Start with default logo immediately to prevent flickering
  const [logoUrl, setLogoUrl] = useState(defaultLogo);
  const hasTriedFallback = useRef(false);
  const customerLogoUrlRef = useRef(null);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const logo = await settingsService.getCurrentLogo();

        if (logo && logo.asset_id) {
          // Build the customer logo URL with tenant context
          try {
            const tenantAlias = getTenantAlias();
            const baseUrl = buildApiUrl(`api/v1/assets/${logo.asset_id}`);
            const url = tenantAlias
              ? `${baseUrl}?tenant=${encodeURIComponent(tenantAlias)}`
              : baseUrl;

            customerLogoUrlRef.current = url;

            // Preload the customer logo image
            const img = new Image();
            img.onload = () => {
              // Only switch if image loads successfully and we haven't tried fallback
              if (!hasTriedFallback.current) {
                setLogoUrl(url);
              }
            };
            img.onerror = () => {
              // Customer logo failed to load, keep default
              hasTriedFallback.current = true;
            };
            img.src = url;
          } catch (error) {
            // If tenant alias can't be determined, try without it
            const baseUrl = buildApiUrl(`api/v1/assets/${logo.asset_id}`);
            customerLogoUrlRef.current = baseUrl;

            // Preload the customer logo image
            const img = new Image();
            img.onload = () => {
              if (!hasTriedFallback.current) {
                setLogoUrl(baseUrl);
              }
            };
            img.onerror = () => {
              hasTriedFallback.current = true;
            };
            img.src = baseUrl;
          }
        }
        // If no logo found, keep default (already set)
      } catch (error) {
        // Error fetching logo or no logo exists, keep default
        // Default is already set, no need to update
      }
    };

    fetchLogo();
  }, []);

  // Handle image load error - switch to default only once
  const handleImageError = e => {
    if (!hasTriedFallback.current && e.target.src !== defaultLogo) {
      hasTriedFallback.current = true;
      setLogoUrl(defaultLogo);
    }
  };

  return {
    logoUrl,
    handleImageError,
  };
};
